import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { artistProfileGet, artistPublication } from "@/services/firebase/artistHub";
import { syncOverwrites } from "@/lib/featured";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { AdminErrorAlert } from "@/components/admin-ui";

const preview = (value: string) => (value ? (value.length > 80 ? `${value.slice(0, 80)}…` : value) : "（空白）");

export default function ArtistPublicationPanel({ artistId, dirty, actorName, actorBio }: {
  artistId: string;
  dirty: boolean;
  /** Saved actors.name / actors.biography — what publishProfile copies onto the App profile. */
  actorName?: string | null;
  actorBio?: string | null;
}) {
  const client = useQueryClient();
  const key = ["artist-publication", artistId];
  const profileKey = ["artist-profile", artistId];
  const status = useQuery({ queryKey: key, queryFn: () => artistPublication("get", artistId), retry: false });
  // Shares the cache with ArtistProfilePanel; only a published profile has App data to overwrite.
  const profile = useQuery({ queryKey: profileKey, queryFn: () => artistProfileGet(artistId), retry: false, enabled: status.data === true });
  const [confirming, setConfirming] = useState(false);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);
  const update = useMutation({
    mutationFn: (action: "publishProfile" | "unpublishProfile") => artistPublication(action, artistId),
    onSuccess: (published) => {
      client.setQueryData(key, published);
      void client.invalidateQueries({ queryKey: profileKey });
    },
  });
  const overwrites = profile.data ? syncOverwrites({ name: actorName, biography: actorBio }, profile.data) : [];
  const published = status.data === true;
  return <Card className="mb-6">
    <CardHeader><CardTitle>App 藝人專區 · 測試環境</CardTitle></CardHeader>
    <CardContent className="space-y-3">
      <p>{status.isPending ? "讀取發布狀態…" : status.isError ? "無法取得發布狀態" : published ? "專頁已公開" : "專頁尚未公開"}</p>
      <p className="text-sm text-muted-foreground">發布會同步已儲存的姓名、照片與簡介。作品只顯示關聯且已上架的短劇；未發布專頁不會出現在 App。</p>
      <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
        {published ? "「同步已儲存資料」" : "「發布藝人專頁」"}會用「短劇演員資料」分頁的演員名稱與簡介，覆蓋 App 專頁上的名稱與簡介，包含藝人在 App 自己改過的內容。
        只想修正 App 專頁文字，請改用「專頁資料」分頁的編輯。
        {published && profile.isSuccess && (overwrites.length
          ? <strong className="mt-1 block">目前有 {overwrites.length} 個欄位不同：{overwrites.map((o) => o.label).join("、")}。</strong>
          : <span className="mt-1 block">目前名稱與簡介一致，同步不會改動文字。</span>)}
      </p>
      {dirty && <p className="text-sm">「短劇演員資料」有未儲存的變更，請先儲存，再同步專頁。</p>}
      {update.error && <AdminErrorAlert message={update.error.message} />}
      {update.isSuccess && <p role="status">發布狀態已更新</p>}
      <div className="flex flex-wrap gap-3">
        {status.isError ? <Button onClick={() => status.refetch()}>重試</Button> : <>
          <Button
            disabled={dirty || status.isPending || update.isPending || (published && profile.isFetching)}
            onClick={() => (published && overwrites.length === 0 && profile.isSuccess ? update.mutate("publishProfile") : setConfirming(true))}
          >
            {update.isPending ? "處理中…" : published ? "同步已儲存資料" : "發布藝人專頁"}
          </Button>
          {published && <Button variant="outline" disabled={update.isPending} onClick={() => setConfirmingUnpublish(true)}>下架專頁</Button>}
        </>}
      </div>
      <ConfirmDialog
        isOpen={confirming}
        title={published ? "同步會覆蓋 App 專頁文字" : "發布藝人專頁"}
        message={overwrites.length
          ? "以下欄位會改成「短劇演員資料」的內容，藝人在 App 改過的文字會被取代："
          : published
            ? "無法讀取 App 專頁目前的內容，無法比對差異。同步會用「短劇演員資料」的名稱與簡介覆蓋 App 專頁。"
            : "會用「短劇演員資料」的名稱、照片與簡介建立或恢復 App 專頁；之前若已有藝人自己改過的名稱與簡介，會被取代。"}
        extra={overwrites.length > 0 && (
          <dl className="mt-2 space-y-2 text-sm">
            {overwrites.map((o) => (
              <div key={o.field} className="rounded-md border p-2">
                <dt className="font-medium">{o.label}</dt>
                <dd className="break-words text-muted-foreground"><span className="line-through">{preview(o.current)}</span></dd>
                <dd className="break-words">→ {preview(o.next)}</dd>
              </div>
            ))}
          </dl>
        )}
        confirmText={published ? "覆蓋並同步" : "發布"}
        variant={overwrites.length ? "danger" : "default"}
        onConfirm={() => {
          setConfirming(false);
          update.mutate("publishProfile");
        }}
        onCancel={() => setConfirming(false)}
      />
      <ConfirmDialog
        isOpen={confirmingUnpublish}
        title="下架藝人專頁"
        message="下架後，此藝人的專頁會從 App 消失，使用者不能再進入藝人頁，認證徽章也一併消失。追蹤關係與內容資料會保留，之後可以再按「發布藝人專頁」恢復。"
        confirmText="下架專頁"
        variant="danger"
        onConfirm={() => {
          setConfirmingUnpublish(false);
          update.mutate("unpublishProfile");
        }}
        onCancel={() => setConfirmingUnpublish(false)}
      />
    </CardContent>
  </Card>;
}
